/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { FavoriteIcon } from '@/components/atoms/FavoriteIcon';
import { InfoTrack } from '@/components/atoms/InfoTrack';
import { Picture } from '@/components/atoms/Picture';
import {
  addFavorite,
  removeFavorite,
  selectFavorite
} from '@/features/favorite/favoriteSlice';
import { setFavorite } from '@/features/playlist/playlistSlice';
import { selectSearchResults } from '@/features/search/searchSlice';
import {
  selectSingleTrack,
  setAutoPlay,
  setCurrentTime,
  setIsUsingCurrentTime
} from '@/features/singleTrack/singleTrackSlice';
import { ITracksId } from '@/interface';
import clx from 'classnames';
import { createRef, useCallback, useEffect, useState } from 'react';
import AudioPlayer, { RHAP_UI } from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import { useNavigate } from 'react-router-dom';
import './audio.scss';
import { saveFavorites, updateFavorites } from '@/services/firebaseApi';
import { useAuthStore } from '@/zustand/useAuthGoogle';

export const AudioFooter = ({ track }: { track: ITracksId | undefined }) => {
  const dispatch = useAppDispatch();
  const { isWriting } = useAppSelector(selectSearchResults);
  const { isAutoPlay, currentTime, isCurrentTime } =
    useAppSelector(selectSingleTrack);
  const { favoriteTracks } = useAppSelector(selectFavorite);
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState<boolean | undefined>(undefined);
  const player = createRef<AudioPlayer>();
  const { user: userGoogle, idFavorite } = useAuthStore((state) => state);

  const addTrackFavorite = useCallback(async () => {
    const removeId = favoriteTracks.items.filter(
      (favorite: any) => favorite.track.id === track?.track.id
    );

    if (!track) return;

    if (
      !track?.track.isFavorite === undefined ||
      !track?.track.isFavorite === false
    ) {
      dispatch(setFavorite(track, false));
      dispatch(removeFavorite(track));
      return;
    }

    if (removeId.length) {
      setIsFavorite(false);
      dispatch(setFavorite(track, false));
      dispatch(removeFavorite(track));
      await updateFavorites(
        userGoogle?.uid,
        favoriteTracks.items.filter((f) => f.id !== track.id),
        userGoogle?.uid
      );
      return;
    }

    const newFavorite = {
      ...track,
      track: {
        ...track.track,
        isFavorite: true
      }
    };
    dispatch(addFavorite(newFavorite));
    setIsFavorite(true);
    if (!userGoogle && !idFavorite) return;
    await saveFavorites(userGoogle?.uid, [
      ...favoriteTracks.items,
      newFavorite
    ]);
  }, [dispatch, favoriteTracks.items, idFavorite, track, userGoogle]);

  const isWritingStyles: React.CSSProperties = {
    opacity: isWriting ? 0 : 1
  };

  const showTrackSingle = () => {
    dispatch(setCurrentTime(player.current?.audio.current?.currentTime || 0));
    navigate(`/single/${track?.track.id}`);
    player.current?.audio?.current?.pause();
  };

  const handlePlay = () => {
    dispatch(setAutoPlay(true));
  };

  useEffect(() => {
    if (!track?.track) return;
    setIsFavorite(track.track.isFavorite);
  }, [track]);

  useEffect(() => {
    if (!isCurrentTime) return;
    if (!player.current?.audio.current) return;
    player.current.audio.current.currentTime = currentTime || 0;
    isAutoPlay && player.current.audio.current.play();
    dispatch(setIsUsingCurrentTime(false));
  }, [currentTime, dispatch, isAutoPlay, isCurrentTime, player]);

  const styleAudio = clx('m-audio__text', {
    marqueeActive: track?.track.name ? track.track.name.length > 15 : false
  });

  return (
    <div className='m-audio' style={isWritingStyles}>
      {track?.track && (
        <AudioPlayer
          // style={stylesBg}
          className='m-audio__player'
          layout='horizontal'
          customVolumeControls={[]}
          customAdditionalControls={[]}
          autoPlay={isAutoPlay}
          src={track?.track?.preview_url}
          hasDefaultKeyBindings={false}
          showJumpControls={false}
          ref={player}
          customControlsSection={[
            <div className={styleAudio} key={track.id}>
              <InfoTrack
                key={track.id}
                isToAudio={true}
                album={track?.track?.name}
                artist={track?.track?.artists[0]?.name}
              />
              <FavoriteIcon
                isFavorite={isFavorite}
                onClick={() => addTrackFavorite()}
              />
            </div>,
            RHAP_UI.MAIN_CONTROLS
          ]}
          showFilledProgress={false}
          showFilledVolume={false}
          showDownloadProgress={false}
          showSkipControls={false}
          customProgressBarSection={[
            <div className='m-audio__img' key={track.id}>
              <Picture
                onClick={() => showTrackSingle()}
                url={track?.track.album.images[0].url || ''}
                alt={track?.track.name || ''}
                classPicture='m-audio__image'
              />
              <Picture
                onClick={() => showTrackSingle()}
                url={track?.track.album.images[0].url || ''}
                alt={track?.track.name || ''}
                classPicture='m-audio__image audio3 m-audio__image--hidden'
              />
            </div>
          ]}
          onPlay={() => handlePlay()}
        />
      )}
    </div>
  );
};
